import { I18N, IS_SSR, Settings, api, navigation } from '@/app'
import {
  Achievement,
  AchievementLevel,
  Answer,
  Chapter,
  CommunityFeed,
  Hurdle,
  HurdleCompare,
  Module,
  Organisation,
  Story,
} from '@/types'
import { analytics } from '../services/analytics'
import { APIClient } from '@/services'
import format from 'date-fns/format'

const handleShared = p => {
  return action => {}
}

export type ShareData = {
  text?: string
  data?: string
  title?: string
  url?: string
  type?: 'link' | 'text'
}

type ShareContent = {
  message: string
  data?: string
  title: string
  type?: ShareData['type']
}

const getWebsite = () => {
  if (IS_SSR) return ''

  return window?.location?.origin
}

const share = async (content: ShareContent) => {
  const isMobileShare = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  const type = content?.type || 'text'

  const shareData: ShareData = {
    text: content?.message,
    [type === 'link' ? 'url' : 'data']: content?.data,
    title: content?.title,
    type,
  }

  const shareable = !!navigator.share && navigator.canShare(shareData) && isMobileShare

  const ShareModal = (await import('../components/Modals/Share')).ShareModal

  let shared = false

  if (!shareable) {
    shared = await ShareModal.request(shareData)
  } else {
    shareData.text = content.message ? content.message : content.data
    await navigator.share(shareData)
    await APIClient.Session.sharedApp()
    shared = true
  }

  return shared
}

export const shareOrganisation = (organisation: Organisation) => {
  const address = `${getWebsite()}/organisations/${organisation.id}`

  const message = I18N.t('share.organisation.message', {
    appName: Settings.AppName,
    address,
    organisation: organisation.name,
  })

  share({
    message,
    data: address,
    title: organisation.name,
    type: 'link',
  }).then(() => {
    handleShared({
      type: 'organisation',
      id: organisation.id,
      name: organisation.name,
    })
    analytics.track('org_share', { name: organisation.name })
  })
}

export const shareReview = (organisation: Organisation, review: Answer) => {
  const address = `${getWebsite()}/organisations/${organisation.id}?tab=reviews&reviewId=${review.id}`

  const message = I18N.t('share.review.message', {
    appName: Settings.AppName,
    address,
    organisation: organisation.name,
  })

  share({
    message,
    data: address,
    title: organisation.name,
    type: 'link',
  }).then(
    handleShared({
      type: 'review',
      id: review.id,
      name: organisation.name,
    }),
  )
}

export const shareStory = (story: Story) => {
  const address = `${getWebsite()}/community/?section=stories&storyId=${story.id}`

  const message = I18N.t('share.story.message', {
    appName: Settings.AppName,
    address,
  })

  share({
    message,
    data: address,
    title: story.headline,
    type: 'link',
  }).then(
    handleShared({
      type: 'successStory',
      id: story.id,
      name: story.headline,
    }),
  )
}

export const shareModule = async (module: Module) => {
  const address = `${getWebsite()}/learn/chapter/${module.id}`

  const message = I18N.t('share.collection.message', {
    appName: Settings.AppName,
    address,
    module: module.title,
  })

  const shared = await share({
    message,
    data: address,
    title: module.title,
    type: 'link',
  })

  if (shared) {
    handleShared({
      type: 'collection',
      id: module.id,
      name: module.title,
    })
  }

  return shared
}

export const shareLessonProgress = async (chapter: Chapter) => {
  const address = `${getWebsite()}/learn/lessonView/${chapter.id}`

  const message = I18N.t('share.lessonProgress.message', {
    appName: Settings.AppName,
    address,
    module: chapter.title,
  })

  const shared = await share({
    message,
    data: address,
    title: chapter.title,
    type: 'link',
  })

  if (shared) {
    handleShared({
      type: 'learnProgress',
      id: chapter.id,
      name: chapter.title,
    })
  }

  return shared
}

export const shareAchievement = (achievement: Achievement) => {
  const address = `${getWebsite()}/`

  const message = I18N.t('share.achievement.message', {
    appName: Settings.AppName,
    achievementName: achievement?.name,
  })
  analytics.track('badge_manual_share')
  share({
    message,
    data: address,
    title: achievement.name,
    type: 'link',
  })
}

export const shareAchievementLevel = (level: AchievementLevel) => {
  const address = `${getWebsite()}/`

  const message = I18N.t('share.achievementLevel.message', {
    levelName: level?.name,
    achievementName: level?.achievement?.name,
  })

  analytics.track('badge_manual_share')

  share({
    message,
    data: address,
    title: level.name,
    type: 'link',
  })
}

export const shareHurdleGame = (hurdle: Hurdle) => {
  const address = `${getWebsite()}/hurdle`

  const emojiMap: Record<HurdleCompare['outcome'] | 'default', string> = {
    correct: '🟩',
    wrong_position: '🟨',
    not_in_word: '⬜',
    default: '⬜',
  }

  const date = format(new Date(), 'yyyy-MM-dd')

  let message = `Hurdle ${date}\n`

  hurdle?.guesses?.forEach(guess => {
    let line = ''
    guess?.comparison?.forEach(letter => {
      line += emojiMap[letter?.outcome] || emojiMap.default
    })
    message += line + '\n'
  })

  message += I18N.t('share.hurdle.message')

  share({
    message,
    title: 'Hurdle',
    data: address,
    type: 'link',
  })
}

export const shareApp = () => {
  Share.share({
    data: '/organisations',
    message: '',
    title: I18N.t('share.app.dialog'),
    type: 'link',
  })
}

export const Share = {
  share,
  getWebsite,
}

export const achievementShareUrl = (level?: AchievementLevel) => {
  const timestamp = new Date().getTime().toString()
  const profileId = APIClient.Session.QueryKeys.me.getData()?.id
  return `${api.config.baseURL}achievements/levels/${level?.id}/share/?ts=${timestamp}&profileId=${profileId}`
}

export const shareCommunityPost = async (post: CommunityFeed) => {
  const address = `${getWebsite()}${navigation.getPathWithParams('Feed.Item', { id: post?.id })}`

  const title = `Check out this cool ${post.type} on Hurd!`

  return await share({
    message: '',
    data: address,
    title,
    type: 'link',
  })
}

export const shareReferralCode = (code: string) => {
  share({
    title: I18N.t('share.referralCode.dialog'),
    message: `Join me on Hurd - sign up here and use my referral code\n${code}\n\nhttps://app.hurd.world/auth/signup`,
  })
}
